import { useNuxtApp } from '#imports'

import type { Bcp47Locale } from '@backmarket/core-types/Locale'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { PURPOSES_ID } from '@backmarket/nuxt-module-tracking/didomi/config'
import { useDataConsent } from '@backmarket/nuxt-module-tracking/useDataConsent'

import type { BrowsingHistoryEntry, CaptureVisitInput } from '../types'
import { buildEntryId, normalizeTerm } from '../utils/normalization'

export function useRecordVisit() {
  const nuxtApp = useNuxtApp()
  const locale = useI18nLocale()
  const experiments = useExperiments()
  const { isDidomiEnabled, getUserConsentForPurpose } = useDataConsent()
  const isCaptureEnabled =
    experiments['experiment.browsingHistoryPanel'] === 'enabled'

  async function recordVisit(input: CaptureVisitInput): Promise<void> {
    if (!import.meta.client || !isCaptureEnabled) return
    if (input.kind === 'search' && input.term.trim() === '') return

    // In opt-in markets (Didomi loaded) only record when the user has granted
    // the personalization purpose. Where Didomi isn't required, consent is
    // implicit and recording stays on.
    if (
      isDidomiEnabled &&
      !getUserConsentForPurpose(PURPOSES_ID.personalization)
    ) {
      return
    }

    const { upsertVisit } = await import('../utils/persistence')

    await upsertVisit(toEntry(input, locale))
    await nuxtApp.callHook('browsing-history:update')
  }

  return { recordVisit }
}

function toEntry(
  input: CaptureVisitInput,
  locale: Bcp47Locale,
): BrowsingHistoryEntry {
  if (input.kind === 'search') {
    return {
      id: buildEntryId('search', locale, normalizeTerm(input.term)),
      kind: 'search',
      locale,
      lastVisitedAt: Date.now(),
      term: input.term.trim(),
    }
  }

  if (input.kind === 'plp') {
    return {
      id: buildEntryId('plp', locale, input.landingId),
      kind: 'plp',
      locale,
      lastVisitedAt: Date.now(),
      landingId: input.landingId,
    }
  }

  return {
    id: buildEntryId('pdp', locale, toPdpReference(input)),
    kind: 'pdp',
    locale,
    lastVisitedAt: Date.now(),
    productId: input.productId,
    grade: input.grade,
    offerType: input.offerType,
  }
}

function toPdpReference(input: Extract<CaptureVisitInput, { kind: 'pdp' }>) {
  return `${input.productId}_${input.grade}_${input.offerType}`
}
